import { rem } from 'polished'
import { useCallback } from 'react'

import { mediaQueryUp } from 'components/utils/breakpoint'
import { INTL_LONG_MONTH_LONG_YEAR } from 'constants/dateFormats'
import type { CalendarMonth } from './Calendar.types'
import { useIntlDateFormatter } from 'lib/datetime/dateUtils'
import styled from 'styled-components'

import { TRIP_MONTH_ATTRIBUTE } from './calendarPanelLayout'

/**
 * A month's name, matching the grid's own default.
 *
 * Owned here rather than left to the calendar for two reasons: months stacked
 * for scrolling need room above their name, or the last week of one sits
 * flush against the next month's title; and the aligner needs something it
 * can address to bring a month to the top of its scroller.
 */
const MonthName = styled.div`
  padding: ${rem(4)} ${rem(10)} ${rem(12)};
  font-size: ${rem(17)};
  font-weight: 700;
  letter-spacing: -0.01em;

  ${mediaQueryUp.tablet} {
    padding: ${rem(4)} ${rem(18)} ${rem(12)};
  }

  &.stacked {
    margin-top: ${rem(24)};
  }
`

/**
 * Renders month names for a trip planner calendar.
 *
 * `stacked` is for `scroll` layout, where one month follows another; a paged
 * calendar shows one at a time and needs no gap.
 */
export function useTripCalendarMonthHeader(stacked: boolean) {
  const dateFormatter = useIntlDateFormatter()

  return useCallback((month: CalendarMonth) => (
    <MonthName
      {...{ [TRIP_MONTH_ATTRIBUTE]: month.key }}
      className={stacked ? 'stacked' : undefined}
    >
      {dateFormatter(new Date(month.year, month.month, 1), INTL_LONG_MONTH_LONG_YEAR)}
    </MonthName>
  ), [dateFormatter, stacked])
}
